import { createContext } from 'react';

/**
 * Derived-query context. Sits on top of the live reducer query and
 * exposes the two values that the rest of the palette should react
 * to instead of the raw per-keystroke input:
 *
 *   - `debouncedQuery`: the live query after debouncing. This is the
 *     value consumer scope-hooks fetch against, so a burst of typing
 *     produces a single fetch rather than one per character. Clearing
 *     the query is NOT debounced -- it resolves to `''` immediately
 *     so consumer children are wiped without a trailing delay.
 *
 *   - `isSearchSettling`: true while the live input is ahead of the
 *     debounced query (i.e. the user has typed but the debounce
 *     window hasn't elapsed yet). Internal-only signal that bridges
 *     the pending indicator across the debounce window: without it,
 *     the spinner couldn't appear until a fetch actually started
 *     (one debounce-window later), leaving a dead gap where the user
 *     typed but nothing visibly acknowledged it.
 *
 * The live, per-keystroke query stays internal (input echo, snap
 * parsing, local ranking). It is intentionally NOT reachable through
 * the public `useCommandPaletteState()` surface, so consumers cannot
 * accidentally key a fetch off the raw input.
 */
export interface CommandPaletteDerivedQueryContextValue {
  debouncedQuery: string;
  isSearchSettling: boolean;
}

export const CommandPaletteDerivedQueryContext = createContext<CommandPaletteDerivedQueryContextValue | null>(null);
